import { motion, useReducedMotion, type Transition } from 'motion/react';
import { ChevronDown } from 'lucide-react';
import { useCallback, useId, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { cn } from '@/lib/utils';

/**
 * Bouncy accordion — adapted from beui.dev/components/motion/bouncy-accordion.
 *
 * Only one row is open at a time; the open row detaches from its neighbours
 * (12px gap) and rounds all four corners, while the rows above and below round
 * the edges they now expose. The spring on that radius + gap is the whole point
 * of the component.
 *
 * Deltas from the upstream source, all deliberate:
 *  - `truncate` dropped from the title. Upstream assumes short one-line labels;
 *    our FAQ questions run to two lines and were being clipped mid-word.
 *  - Trigger is `items-start` with vertical padding rather than a fixed
 *    `min-h-[54px]` centred row, for the same reason.
 *  - Corner radius is a prop (`radius`) instead of a hard-coded 28px, so it can
 *    sit at the page's own scale.
 *  - `EASE_OUT` is inlined rather than pulling in upstream's `lib/ease.ts`; it
 *    is the same curve already used as `--ease-out` in index.css.
 */

/** cubic-bezier(0.16, 1, 0.3, 1) — matches `--ease-out` in index.css. */
const EASE_OUT = [0.16, 1, 0.3, 1] as const;

export type BouncyAccordionItem = {
  id: string;
  title: ReactNode;
  description?: ReactNode;
  icon?: ReactNode;
  disabled?: boolean;
};

export type BouncyAccordionClassNames = {
  root?: string;
  item?: string;
  trigger?: string;
  icon?: string;
  title?: string;
  chevron?: string;
  content?: string;
  description?: string;
};

export interface BouncyAccordionProps {
  items: BouncyAccordionItem[];
  value?: string | null;
  defaultValue?: string | null;
  onValueChange?: (value: string | null) => void;
  collapsible?: boolean;
  /** Corner radius, px, applied to the exposed edges of each group. */
  radius?: number;
  className?: string;
  classNames?: BouncyAccordionClassNames;
}

// Gap spring must not overshoot y — positive overshoot drifts rows below their
// resting point and briefly overlaps the next one.
const ROW_TRANSITION: Transition = { type: 'spring', duration: 0.55, bounce: 0.38 };
const CONTENT_OPEN_TRANSITION: Transition = { type: 'spring', duration: 0.58, bounce: 0.32 };
const CONTENT_CLOSE_TRANSITION: Transition = { type: 'spring', duration: 0.46, bounce: 0.26 };
const DESCRIPTION_TRANSITION: Transition = { duration: 0.18, ease: EASE_OUT };
const CHEVRON_TRANSITION: Transition = { type: 'spring', duration: 0.42, bounce: 0.28 };

function useControllableAccordionValue({
  value,
  defaultValue,
  onValueChange,
}: {
  value?: string | null;
  defaultValue?: string | null;
  onValueChange?: (value: string | null) => void;
}) {
  const [internalValue, setInternalValue] = useState(defaultValue ?? null);
  const isControlled = value !== undefined;
  const currentValue = value ?? internalValue;

  const setValue = useCallback(
    (next: string | null) => {
      if (!isControlled) setInternalValue(next);
      onValueChange?.(next);
    },
    [isControlled, onValueChange],
  );

  return [currentValue, setValue] as const;
}

function BouncyAccordionRow({
  item,
  open,
  startsGroup,
  endsGroup,
  separatedFromPrevious,
  contentId,
  triggerId,
  reduce,
  radius,
  classNames,
  onToggle,
}: {
  item: BouncyAccordionItem;
  open: boolean;
  startsGroup: boolean;
  endsGroup: boolean;
  separatedFromPrevious: boolean;
  contentId: string;
  triggerId: string;
  reduce: boolean | null;
  radius: number;
  classNames?: BouncyAccordionClassNames;
  onToggle: () => void;
}) {
  const contentRef = useRef<HTMLDivElement>(null);
  const [contentHeight, setContentHeight] = useState(0);

  useLayoutEffect(() => {
    const node = contentRef.current;
    if (!node) return;

    const updateHeight = () => setContentHeight(node.offsetHeight);
    updateHeight();

    const observer = new ResizeObserver(updateHeight);
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  return (
    <motion.div
      layout="position"
      initial={false}
      style={{ marginTop: separatedFromPrevious ? 12 : 0 }}
      transition={reduce ? { duration: 0 } : ROW_TRANSITION}
    >
      <motion.div
        data-state={open ? 'open' : 'closed'}
        initial={false}
        animate={{
          borderTopLeftRadius: startsGroup ? radius : 0,
          borderTopRightRadius: startsGroup ? radius : 0,
          borderBottomLeftRadius: endsGroup ? radius : 0,
          borderBottomRightRadius: endsGroup ? radius : 0,
        }}
        transition={reduce ? { duration: 0 } : ROW_TRANSITION}
        className={cn(
          'overflow-hidden bg-card text-card-foreground',
          item.disabled && 'opacity-50',
          classNames?.item,
        )}
      >
        <button
          id={triggerId}
          type="button"
          disabled={item.disabled}
          aria-expanded={open}
          aria-controls={contentId}
          onClick={onToggle}
          className={cn(
            'flex w-full cursor-pointer items-start gap-4 px-5 py-4 text-left outline-none transition-colors',
            'focus-visible:bg-muted/25',
            'disabled:pointer-events-none',
            classNames?.trigger,
          )}
        >
          {item.icon ? (
            <span
              className={cn(
                'grid h-7 w-7 shrink-0 place-items-center text-muted-foreground',
                classNames?.icon,
              )}
            >
              {item.icon}
            </span>
          ) : null}
          <span className={cn('min-w-0 flex-1 font-medium text-foreground', classNames?.title)}>
            {item.title}
          </span>
          <motion.span
            aria-hidden
            animate={{ rotate: open ? 180 : 0 }}
            transition={reduce ? { duration: 0 } : CHEVRON_TRANSITION}
            className={cn(
              'grid h-6 w-6 shrink-0 place-items-center text-muted-foreground',
              classNames?.chevron,
            )}
          >
            <ChevronDown className="h-4 w-4" />
          </motion.span>
        </button>

        <motion.div
          layout="size"
          id={contentId}
          role="region"
          aria-labelledby={triggerId}
          aria-hidden={!open}
          inert={!open}
          initial={false}
          style={{ height: open && item.description ? contentHeight : 0 }}
          transition={
            reduce ? { duration: 0 } : open ? CONTENT_OPEN_TRANSITION : CONTENT_CLOSE_TRANSITION
          }
          className={cn('overflow-hidden', classNames?.content)}
        >
          <motion.div
            ref={contentRef}
            animate={{ opacity: open ? 1 : 0 }}
            transition={reduce ? { duration: 0 } : DESCRIPTION_TRANSITION}
            className="px-5 pb-5"
          >
            <div className={cn('leading-relaxed text-muted-foreground', classNames?.description)}>
              {item.description}
            </div>
          </motion.div>
        </motion.div>
      </motion.div>
    </motion.div>
  );
}

export function BouncyAccordion({
  items,
  value,
  defaultValue = null,
  onValueChange,
  collapsible = true,
  radius = 28,
  className,
  classNames,
}: BouncyAccordionProps) {
  const reduce = useReducedMotion();
  const baseId = useId();
  const [activeValue, setActiveValue] = useControllableAccordionValue({
    value,
    defaultValue,
    onValueChange,
  });
  const activeIndex = items.findIndex((item) => item.id === activeValue);

  const toggleItem = useCallback(
    (id: string) => {
      if (activeValue === id) {
        if (collapsible) setActiveValue(null);
        return;
      }
      setActiveValue(id);
    },
    [activeValue, collapsible, setActiveValue],
  );

  return (
    <div className={cn('w-full', className, classNames?.root)}>
      {items.map((item, index) => {
        const open = activeValue === item.id;
        const previousIsOpen = activeIndex === index - 1;
        const nextIsOpen = activeIndex === index + 1;
        const startsGroup = open || index === 0 || previousIsOpen;
        const endsGroup = open || index === items.length - 1 || nextIsOpen;
        const separatedFromPrevious = index > 0 && (open || previousIsOpen);

        return (
          <BouncyAccordionRow
            key={item.id}
            item={item}
            open={open}
            startsGroup={startsGroup}
            endsGroup={endsGroup}
            separatedFromPrevious={separatedFromPrevious}
            contentId={`${baseId}-${item.id}-content`}
            triggerId={`${baseId}-${item.id}-trigger`}
            reduce={reduce}
            radius={radius}
            classNames={classNames}
            onToggle={() => toggleItem(item.id)}
          />
        );
      })}
    </div>
  );
}
